export const SOCIAL_NETWORKS = ['instagram', 'facebook', 'tiktok', 'linkedin', 'x'] as const

export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number]

export type SocialLinks = Partial<Record<SocialNetwork, string>>

interface SocialNetworkMeta {
  label: string
  hosts: string[]
  profileBase: string
  placeholder: string
  example: string
}

export const SOCIAL_NETWORK_META: Record<SocialNetwork, SocialNetworkMeta> = {
  instagram: {
    label: 'Instagram',
    hosts: ['instagram.com'],
    profileBase: 'https://www.instagram.com/',
    placeholder: '@suaimobiliaria ou instagram.com/suaimobiliaria',
    example: 'https://www.instagram.com/suaimobiliaria'
  },
  facebook: {
    label: 'Facebook',
    hosts: ['facebook.com', 'fb.com', 'fb.me'],
    profileBase: 'https://www.facebook.com/',
    placeholder: 'facebook.com/suaimobiliaria',
    example: 'https://www.facebook.com/suaimobiliaria'
  },
  tiktok: {
    label: 'TikTok',
    hosts: ['tiktok.com'],
    profileBase: 'https://www.tiktok.com/@',
    placeholder: '@suaimobiliaria ou tiktok.com/@suaimobiliaria',
    example: 'https://www.tiktok.com/@suaimobiliaria'
  },
  linkedin: {
    label: 'LinkedIn',
    hosts: ['linkedin.com', 'lnkd.in'],
    profileBase: 'https://www.linkedin.com/company/',
    placeholder: 'linkedin.com/company/suaimobiliaria',
    example: 'https://www.linkedin.com/company/suaimobiliaria'
  },
  x: {
    label: 'X (Twitter)',
    hosts: ['x.com', 'twitter.com'],
    profileBase: 'https://x.com/',
    placeholder: '@suaimobiliaria ou x.com/suaimobiliaria',
    example: 'https://x.com/suaimobiliaria'
  }
}

const HANDLE = /^@?[\w.-]{1,100}$/

const belongsTo = (hostname: string, hosts: string[]) => hosts.some(host => hostname === host || hostname.endsWith(`.${host}`))

const looksLikeHandle = (value: string) =>
  HANDLE.test(value) &&
  (value.startsWith('@') || !SOCIAL_NETWORKS.some(network => SOCIAL_NETWORK_META[network].hosts.some(host => value.includes(host))))

export function normalizeSocialUrl(network: SocialNetwork, input: string): string {
  const value = input.trim()
  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value
  if (looksLikeHandle(value)) return `${SOCIAL_NETWORK_META[network].profileBase}${value.replace(/^@/, '')}`
  return `https://${value.replace(/^\/+/, '')}`
}

export function socialUrlError(network: SocialNetwork, value: string): string | null {
  if (!value) return null
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return 'Endereço inválido. Cole o link do perfil ou digite o @.'
  }
  if (!/^https?:$/.test(url.protocol)) return 'Endereço inválido. Cole o link do perfil ou digite o @.'
  const { label, hosts } = SOCIAL_NETWORK_META[network]
  if (!belongsTo(url.hostname.toLowerCase(), hosts)) return `Esse link não é do ${label}.`
  if (url.pathname.replace(/\/+$/, '') === '') return 'Falta o perfil no link (ex.: o @ da imobiliária).'
  if (value.length > 300) return 'Link longo demais.'
  return null
}

export function socialLinksOf(links: SocialLinks | null | undefined): Record<SocialNetwork, string> {
  return Object.fromEntries(SOCIAL_NETWORKS.map(network => [network, links?.[network]?.trim() ?? ''])) as Record<SocialNetwork, string>
}

export function filledSocialNetworks(links: SocialLinks | null | undefined): SocialNetwork[] {
  return SOCIAL_NETWORKS.filter(network => !!links?.[network]?.trim())
}
